import { Button, EmptyState, ErrorState, LoadingState } from "@jod/ui";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, redirect } from "@tanstack/react-router";
import { ConnectivityBanner } from "../features/connectivity/connectivity-banner.tsx";
import { useT } from "../features/i18n/index.ts";
import { useLiff } from "../features/liff/use-liff.ts";
import { resolveConsentGuardRedirect } from "../features/session/route-guard.ts";
import { SessionProvider } from "../features/session/session-context.tsx";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // P0 consent/age-gate gate: no route other than /welcome, /age-gate (and
  // this root's own loading/error/unavailable states) may render before the
  // customer has accepted the age-gate + privacy consent. See
  // features/session/route-guard.ts.
  beforeLoad: ({ location }) => {
    const redirectTo = resolveConsentGuardRedirect(location.pathname);
    if (redirectTo) {
      throw redirect({ to: redirectTo });
    }
  },
  component: RootComponent,
});

/** Gates the whole app on LIFF state: loading / error / external-browser / ready. */
function RootComponent() {
  const t = useT();
  const liff = useLiff();

  if (liff.status === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center">
        <LoadingState label={t("root.connecting")} />
      </div>
    );
  }

  if (liff.status === "error") {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <ErrorState title={t("root.lineConnectFailed")} description={liff.error ?? undefined} />
      </div>
    );
  }

  if (liff.status === "unavailable") {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <EmptyState
          title={t("root.openInLineTitle")}
          description={t("root.openInLineDesc")}
          action={<Button onClick={liff.login}>{t("root.loginWithLine")}</Button>}
        />
      </div>
    );
  }

  return (
    <SessionProvider>
      <ConnectivityBanner />
      <Outlet />
    </SessionProvider>
  );
}
